import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Check, Rocket, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { fetchSetupReport } from "@/lib/api";
import { SETUP_STEPS } from "./setupRules";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

function Kpi({ label, value, tone, testId }) {
  return (
    <div className={`rounded-xl px-3 py-2.5 ${tone}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</div>
      <div className="text-xl font-bold leading-tight">{value}</div>
    </div>
  );
}

function Timeline({ steps }) {
  return (
    <div className="flex items-center gap-1" data-testid="setup-report-timeline">
      {SETUP_STEPS.map((s, i) => {
        const at = steps?.[s.key];
        return (
          <div key={s.key} className="flex items-center">
            <span title={`${s.title}${at ? ` · ${fmt(at)}` : " · not yet"}`}
              className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold ${at ? "bg-emerald-500 text-white" : "bg-zinc-200 text-zinc-500"}`}>
              {at ? <Check size={11} strokeWidth={3} /> : s.n}
            </span>
            {i < SETUP_STEPS.length - 1 && <span className={`h-0.5 w-2 ${at ? "bg-emerald-400" : "bg-zinc-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

const STATUS_FILTERS = [["all", "All"], ["live", "Live"], ["progress", "In progress"], ["none", "Not started"]];
const statusOf = (d) => (d.launched ? "live" : d.done_count ? "progress" : "none");

export function SetupReportBody({ recId, active = true, preloaded = null }) {
  const [data, setData] = useState(preloaded);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  useEffect(() => {
    if (preloaded) { setData(preloaded); return; }
    if (!active || !recId) return;
    setData(null);
    fetchSetupReport(recId).then(setData).catch(() => setData({ error: true }));
  }, [active, recId, preloaded]);

  const rates = (data?.step_rates || []).map((r) => ({ ...r, name: SETUP_STEPS.find((s) => s.key === r.key)?.title || r.key }));
  const needle = q.trim().toLowerCase();
  const rows = (data?.dealers || []).filter((d) => (status === "all" || statusOf(d) === status) && (!needle || `${d.name} ${d.company} ${d.role}`.toLowerCase().includes(needle)));
  return (
    <div data-testid="setup-report">
        <div className="mb-3">
          <div className="flex items-center gap-2 font-semibold text-zinc-900"><Rocket size={16} className="text-violet-600" /> Onboarding report</div>
          <div className="text-xs text-zinc-500">{data?.is_admin ? "All active dealers" : "You and the dealers on your team"} — who completed setup and when.</div>
        </div>
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
        ) : data.error ? (
          <div className="py-8 text-center text-sm text-zinc-500">Couldn't load the report.</div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Kpi label="Dealers" value={data.summary.total} tone="bg-zinc-100 text-zinc-800" testId="setup-report-total" />
              <Kpi label="Live" value={data.summary.launched} tone="bg-emerald-50 text-emerald-800" testId="setup-report-launched" />
              <Kpi label="In progress" value={data.summary.in_progress} tone="bg-violet-50 text-violet-800" testId="setup-report-progress" />
              <Kpi label="Not started" value={data.summary.not_started} tone="bg-amber-50 text-amber-800" testId="setup-report-notstarted" />
            </div>

            <div>
              <div className="text-xs font-semibold text-zinc-700 mb-1">Step completion</div>
              <div className="h-44 rounded-xl border border-zinc-200 p-2" data-testid="setup-report-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rates} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => [`${v}%`, "completed"]} />
                    <Bar dataKey="pct" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {data.launches_by_week.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-zinc-700 mb-1">Launches per week</div>
                <div className="h-36 rounded-xl border border-zinc-200 p-2" data-testid="setup-report-weeks">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.launches_by_week} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#059669" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                <div className="text-xs font-semibold text-zinc-700 flex-1">Timeline by dealer <span className="text-zinc-400 font-normal">({rows.length})</span></div>
                <div className="flex gap-1 rounded-lg bg-zinc-100 p-0.5" data-testid="setup-report-status-filter">
                  {STATUS_FILTERS.map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setStatus(v)} className={`px-2.5 h-7 rounded-md text-[11px] font-semibold ${status === v ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`} data-testid={`setup-report-filter-${v}`}>{l}</button>
                  ))}
                </div>
                <div className="relative sm:w-48">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dealer…" className="h-8 pl-7 rounded-lg text-xs" data-testid="setup-report-search" />
                </div>
              </div>
              <ul className="space-y-2" data-testid="setup-report-list">
                {rows.length === 0 && <li className="text-xs text-zinc-500 py-4 text-center">No dealers match.</li>}
                {rows.slice(0, 100).map((d) => (
                  <li key={d.id} className={`rounded-xl border px-3 py-2.5 ${d.is_self ? "border-violet-300 bg-violet-50/50" : "border-zinc-200 bg-white"}`} data-testid={`setup-report-row-${d.id}`}>
                    <div className="flex items-center gap-3">
                      {d.photo ? <img src={d.photo} alt="" className="h-9 w-9 rounded-full object-cover bg-zinc-200" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} /> : <div className="h-9 w-9 rounded-full bg-zinc-200" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-zinc-900 truncate">{d.name}{d.is_self ? " (you)" : ""}{d.company ? <span className="font-normal text-zinc-500"> · {d.company}</span> : null}</div>
                        <div className="text-[11px] text-zinc-500">
                          {d.launched ? `Live since ${fmt(d.launched_at)}` : d.done_count ? `${d.done_count}/${d.total} steps · last activity ${fmt(d.last_activity)}` : "Not started"}
                          {d.role ? ` · ${d.role}` : ""}
                        </div>
                      </div>
                      <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${d.launched ? "bg-emerald-100 text-emerald-700" : d.done_count ? "bg-violet-100 text-violet-700" : "bg-zinc-100 text-zinc-500"}`}>
                        {d.launched ? "LIVE" : `${Math.round((100 * d.done_count) / d.total)}%`}
                      </span>
                    </div>
                    <div className="mt-2"><Timeline steps={d.steps} /></div>
                  </li>
                ))}
                {rows.length > 100 && <li className="text-[11px] text-zinc-400 text-center py-2">Showing first 100 — narrow with search or a status filter.</li>}
              </ul>
            </div>
          </div>
        )}
    </div>
  );
}

export default function SetupReportDialog({ open, onOpenChange, recId }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="setup-report-dialog">
        <DialogHeader className="sr-only"><DialogTitle>Onboarding report</DialogTitle><DialogDescription>Who completed setup and when</DialogDescription></DialogHeader>
        <SetupReportBody recId={recId} active={open} />
      </DialogContent>
    </Dialog>
  );
}

// Inline version for the Analytics report (loads like every other report section).
export function SetupReportInline({ recId, data }) {
  return (
    <div className="mt-3 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="analytics-onboarding">
      <SetupReportBody recId={recId} preloaded={data} />
    </div>
  );
}

// Hook for the Analytics KPI card: summary of dealer onboarding (admin → all, dealer → team).
export function useSetupReport(recId) {
  const [data, setData] = useState(null);
  useEffect(() => { if (recId) fetchSetupReport(recId).then(setData).catch(() => setData(null)); }, [recId]);
  return data;
}

